import { AlertTriangle } from "lucide-react";
import { useId } from "react";

import { cx } from "@/components/ui";

/** The customer's answer to "save this card?" — `null` until they pick one (nothing is pre-ticked). */
export type SaveChoice = "save" | "once" | null;

const OPTIONS = [
  {
    value: "save",
    title: "Save this card for future payments",
    body: (processor: string) =>
      `${processor}, our payment provider, holds the card details securely. CortexGrip only keeps the card type, last four digits and expiry date. You can remove it any time in Billing.`,
  },
  {
    value: "once",
    title: "Don't save — use it for this payment only",
    body: () => "The card is used for this payment, then removed. Nothing is kept for later.",
  },
] as const;

/**
 * The explicit "Save this card for future payments" choice for domain purchases and renewals. It is
 * only about keeping the card: auto-renew is a separate setting on the domain. Choosing not to save
 * shows the renewal-risk warning straight away; the customer can still change their mind until they
 * pay.
 */
export function SaveCardChoice({
  value,
  onChange,
  processor,
  error,
}: {
  value: SaveChoice;
  onChange: (next: Exclude<SaveChoice, null>) => void;
  processor: string;
  error?: string | null;
}) {
  const name = useId();
  const errorId = useId();
  return (
    <fieldset className="flex min-w-0 flex-col gap-2" aria-describedby={error ? errorId : undefined}>
      <legend className="mb-2 text-[13px] font-semibold text-fg">Save this card for future payments?</legend>
      {OPTIONS.map((o) => {
        const active = value === o.value;
        return (
          <label
            key={o.value}
            className={cx(
              "flex cursor-pointer items-start gap-3 rounded-xl border p-3 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500/40",
              active
                ? "border-brand-500 bg-brand-50"
                : error
                  ? "border-[#d6342f]"
                  : "border-border hover:bg-muted",
            )}
          >
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={active}
              onChange={() => onChange(o.value)}
              className="sr-only"
            />
            <span
              aria-hidden
              className={cx(
                "mt-0.5 size-4 shrink-0 rounded-full border-2",
                active ? "border-brand-500 bg-brand-500 shadow-[inset_0_0_0_2px_white]" : "border-[#d4cac4]",
              )}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-fg">{o.title}</span>
              <span className="mt-0.5 block text-xs text-fg-subtle">{o.body(processor)}</span>
            </span>
          </label>
        );
      })}
      {error && (
        <p id={errorId} className="text-xs text-[#b8291f]">
          {error}
        </p>
      )}
      <div aria-live="polite">{value === "once" && <RenewalRiskWarning />}</div>
    </fieldset>
  );
}

/** Shown whenever a domain is paid for with a card that isn't being saved. */
export function RenewalRiskWarning({ className }: { className?: string }) {
  return (
    <div role="note" className={cx("flex gap-2.5 rounded-xl bg-[#fef4e5] p-3.5 text-xs", className)}>
      <AlertTriangle className="mt-px size-4 shrink-0 text-[#995c05]" aria-hidden />
      <div className="min-w-0">
        <p className="font-semibold text-[#995c05]">Your domain renewal may need you to pay manually</p>
        <p className="mt-1 text-[#5c5551]">
          Without a saved payment method, you may need to pay manually to renew your domain. Missing the
          renewal deadline could cause your domain to expire and interrupt your website or email services. You
          can continue without saving your card.
        </p>
      </div>
    </div>
  );
}
